/**
 * The estimation engine behind /estimate.
 *
 * Everything here is deterministic: a feature catalogue with hour bands, and
 * a set of multipliers for scope, quality and delivery choices. The AI step
 * (api/estimate.js) only proposes a *feature list* tailored to the visitor's
 * description — the hours, timeline and cost always come from this file, so
 * two visitors with the same inputs get the same numbers and nothing is
 * hallucinated into a quote.
 *
 * Hour bands are for a senior team that has shipped this kind of system
 * before. They are ranges on purpose: a single number would be a lie.
 */

export const DEFAULT_RATE = 40 // USD per hour, editable on the result screen

export const PROJECT_TYPES = [
  { id: 'ml', ml: true, serviceSlug: 'ai-development' },
  { id: 'genai', ml: true, serviceSlug: 'generative-ai' },
  { id: 'rag', ml: true, serviceSlug: 'rag' },
  { id: 'agents', ml: true, serviceSlug: 'ai-agents' },
  { id: 'cv', ml: true, serviceSlug: 'computer-vision' },
  { id: 'chatbot', ml: true, serviceSlug: 'nlp-chatbots' },
  { id: 'data', ml: false, serviceSlug: 'data-engineering' },
  { id: 'web', ml: false, serviceSlug: 'web-development' },
  { id: 'mobile', ml: false, serviceSlug: 'mobile-development' },
]

export const INDUSTRIES = ['healthcare', 'fintech', 'ecommerce', 'architecture', 'logistics', 'education', 'saas', 'other']
export const PLATFORMS = ['web', 'ios', 'android', 'api']
export const STAGES = ['idea', 'prototype', 'production']
export const DATA_STATES = ['labelled', 'raw', 'none']
export const UI_LEVELS = ['minimal', 'standard', 'premium']
export const QA_LEVELS = ['basic', 'standard', 'extensive']
export const PRIORITIES = ['balanced', 'fast']

// category: 'ai' | 'core' | 'ui' | 'integration' | 'ops'
const f = (id, name, min, max, category, on = true) => ({ id, name, hours: [min, max], category, on })

const COMMON = [
  f('auth', 'User accounts, roles & authentication', 24, 40, 'core'),
  f('admin', 'Admin panel', 40, 80, 'ui', false),
  f('notif', 'Email & push notifications', 16, 32, 'integration', false),
  f('payments', 'Payments & subscriptions', 40, 80, 'integration', false),
  f('dashboard', 'Reporting dashboard', 40, 80, 'ui', false),
  f('crm', 'CRM / ERP integration', 32, 64, 'integration', false),
  f('i18n', 'Multi-language interface', 16, 32, 'ui', false),
  f('files', 'File upload & storage', 16, 32, 'core', false),
  f('partner-api', 'Public API for partners', 32, 60, 'core', false),
]

const BY_TYPE = {
  ml: [
    f('data-pipeline', 'Data audit & preparation pipeline', 40, 80, 'ai'),
    f('training', 'Baseline & model training', 80, 160, 'ai'),
    f('eval', 'Model evaluation & tuning against the agreed metric', 40, 80, 'ai'),
    f('inference', 'Prediction API or batch job', 32, 56, 'ai'),
    f('monitoring', 'Accuracy & data-drift monitoring', 24, 48, 'ops'),
    f('retrain', 'Automated retraining pipeline', 32, 64, 'ops', false),
    f('explain', 'Explainability for predictions', 24, 48, 'ai', false),
    f('ui', 'Interface for the people using the predictions', 40, 80, 'ui'),
  ],
  genai: [
    f('model-select', 'Model selection & prompt design', 24, 48, 'ai'),
    f('llm-backend', 'LLM application backend', 48, 96, 'ai'),
    f('evalset', 'Evaluation set & automatic quality scoring', 32, 64, 'ai'),
    f('guardrails', 'Guardrails, validation & human review step', 32, 64, 'ai'),
    f('cost', 'Cost & usage tracking per request', 16, 32, 'ops'),
    f('finetune', 'Fine-tuning an open model', 60, 120, 'ai', false),
    f('docs', 'Document processing & extraction', 48, 96, 'ai', false),
    f('ui', 'User interface', 40, 80, 'ui'),
  ],
  rag: [
    f('ingest', 'Document ingestion & sync pipeline', 40, 80, 'ai'),
    f('search', 'Vector & hybrid search with re-ranking', 40, 72, 'ai'),
    f('answer', 'Answer generation with source citations', 40, 72, 'ai'),
    f('chat-ui', 'Chat interface', 32, 64, 'ui'),
    f('acl', 'Per-document access control', 24, 48, 'core', false),
    f('evalset', 'Evaluation set & accuracy tracking', 32, 56, 'ai'),
    f('connectors', 'Connectors (Confluence, Notion, Drive, SharePoint)', 24, 48, 'integration', false),
  ],
  agents: [
    f('workflow', 'Workflow design & tool definitions', 40, 80, 'ai'),
    f('runtime', 'Agent runtime & orchestration', 60, 120, 'ai'),
    f('integrations', 'Integrations with your systems', 40, 80, 'integration'),
    f('approval', 'Human approval steps', 24, 48, 'core'),
    f('audit', 'Monitoring & audit log of every action', 24, 48, 'ops'),
    f('safety', 'Evaluation & safety tests', 32, 64, 'ai'),
    f('ui', 'Operator console', 32, 64, 'ui', false),
  ],
  cv: [
    f('dataset', 'Dataset preparation & labelling tooling', 40, 80, 'ai'),
    f('training', 'Model training (detection / segmentation / classification)', 80, 160, 'ai'),
    f('eval', 'Evaluation on held-out data', 32, 64, 'ai'),
    f('inference', 'Inference service (real-time or batch)', 40, 80, 'ai'),
    f('edge', 'Edge / on-device deployment', 48, 96, 'ops', false),
    f('review-ui', 'Annotation & review interface', 40, 80, 'ui', false),
    f('monitoring', 'Monitoring in production', 24, 48, 'ops'),
  ],
  chatbot: [
    f('design', 'Conversation design', 24, 48, 'ai'),
    f('nlu', 'Language understanding / LLM backend', 48, 96, 'ai'),
    f('kb', 'Knowledge base integration', 32, 64, 'ai'),
    f('channels', 'Channels (web widget, WhatsApp, Telegram)', 24, 48, 'integration'),
    f('handoff', 'Hand-off to a human agent', 16, 32, 'core'),
    f('analytics', 'Conversation analytics', 24, 40, 'ops', false),
    f('multilingual', 'Multilingual answers', 24, 48, 'ai', false),
  ],
  data: [
    f('model', 'Data model & warehouse', 48, 96, 'core'),
    f('etl', 'ETL / ELT pipelines', 60, 120, 'core'),
    f('quality', 'Data quality checks & alerts', 24, 48, 'ops'),
    f('bi', 'BI dashboards', 40, 80, 'ui'),
    f('governance', 'Access control & governance', 24, 48, 'core', false),
    f('streaming', 'Real-time streaming', 48, 96, 'core', false),
  ],
  web: [
    f('ux', 'UX flows & design system', 40, 80, 'ui'),
    f('frontend', 'Frontend application', 80, 160, 'ui'),
    f('backend', 'Backend & database', 80, 160, 'core'),
    f('cms', 'Admin / CMS', 40, 80, 'ui', false),
    f('search', 'Search & filters', 24, 48, 'core', false),
    f('seo', 'SEO & performance', 16, 32, 'ops'),
    f('realtime', 'Real-time features (chat, live updates)', 32, 64, 'core', false),
  ],
  mobile: [
    f('ux', 'UX flows & design system', 40, 80, 'ui'),
    f('app', 'Mobile application (one platform)', 100, 200, 'ui'),
    f('backend', 'Backend & API', 80, 160, 'core'),
    f('offline', 'Offline mode & sync', 32, 64, 'core', false),
    f('push', 'Push notifications', 16, 32, 'integration'),
    f('store', 'App Store & Google Play publishing', 12, 24, 'ops'),
    f('iap', 'In-app purchases', 32, 64, 'integration', false),
  ],
}

// Feature list for a project type: type-specific features first, then the
// common ones nobody remembers to budget for.
export const catalogFor = (typeId) => {
  const specific = BY_TYPE[typeId] || BY_TYPE.web
  const ids = new Set(specific.map((x) => x.id))
  return [...specific, ...COMMON.filter((x) => !ids.has(x.id))].map((x) => ({ ...x, hours: [...x.hours] }))
}

// AI-proposed features arrive without ids or categories we control — give
// them both so toggling and the breakdown work the same way.
export const normaliseAiFeatures = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((x) => x && x.name && Number.isFinite(x.hoursMin) && Number.isFinite(x.hoursMax))
    .slice(0, 14)
    .map((x, i) => ({
      id: `ai-${i}`,
      name: String(x.name).slice(0, 90),
      description: x.description ? String(x.description).slice(0, 220) : '',
      hours: [Math.max(4, Math.round(x.hoursMin)), Math.max(Math.round(x.hoursMin) + 4, Math.round(x.hoursMax))],
      category: ['ai', 'core', 'ui', 'integration', 'ops'].includes(x.category) ? x.category : 'core',
      on: x.recommended !== false,
      ai: true,
    }))

const round = (n) => Math.round(n)
const scale = ([a, b], k) => [round(a * k), round(b * k)]
const add = ([a, b], [c, d]) => [a + c, b + d]

/**
 * Turn the visitor's choices into hours, weeks, cost and a breakdown.
 * `input` shape: see INITIAL_INPUT in the page. Returns null until a project
 * type is chosen.
 */
export const estimate = (input) => {
  const type = PROJECT_TYPES.find((t) => t.id === input.projectType)
  if (!type) return null

  const platforms = input.platforms.length ? input.platforms : ['web']
  const uiFactor = { minimal: 0.85, standard: 1, premium: 1.3 }[input.uiLevel] || 1
  const qaFactor = { basic: 0.1, standard: 0.18, extensive: 0.28 }[input.qaLevel] || 0.18

  const lines = []
  const push = (name, hours, category, key) => lines.push({ key, name, hours, category })

  // 1. Selected features, UI-heavy ones scaled by the chosen finish level.
  input.features
    .filter((x) => x.on)
    .forEach((x) => push(x.name, x.category === 'ui' ? scale(x.hours, uiFactor) : [...x.hours], x.category, x.id))

  // 2. Platforms. Mobile projects already count one app; a second store
  //    platform on a shared codebase is not a second app.
  const native = platforms.filter((p) => p === 'ios' || p === 'android')
  if (type.id === 'mobile') {
    if (native.length === 2) push('Second mobile platform (shared codebase)', scale([100, 200], 0.25 * uiFactor), 'ui', 'platform-2')
    if (platforms.includes('web')) push('Web version', scale([60, 120], uiFactor), 'ui', 'platform-web')
  } else {
    native.forEach((p, i) => push(`Mobile client (${p === 'ios' ? 'iOS' : 'Android'})`, scale([60, 120], (i === 0 ? 1 : 0.6) * uiFactor), 'ui', `platform-${p}`))
  }
  // API-only products skip the interface work entirely.
  const apiOnly = platforms.length === 1 && platforms[0] === 'api'
  if (apiOnly) {
    const kept = lines.filter((l) => l.category !== 'ui')
    lines.length = 0
    lines.push(...kept)
  }

  // 3. Where the project starts.
  if (input.stage === 'idea') push('Discovery & scoping workshop', [24, 40], 'core', 'stage-idea')
  if (input.stage === 'production') push('Integration into the existing system', [32, 64], 'integration', 'stage-prod')
  const stageFactor = input.stage === 'prototype' ? 0.9 : 1

  // 4. Data — the usual reason ML projects slip.
  if (type.ml) {
    if (input.dataState === 'raw') push('Data cleaning & labelling', [40, 80], 'ai', 'data-raw')
    if (input.dataState === 'none') push('Data collection plan & first labelled set', [60, 120], 'ai', 'data-none')
  }

  // 5. Premium finish means a real design phase, not just nicer CSS.
  if (input.uiLevel === 'premium' && !apiOnly) push('Custom UI design & prototyping', [40, 80], 'ui', 'ui-premium')

  // 6. Always: getting it live.
  push('Deployment, CI/CD & environments', [24, 48], 'ops', 'ops-deploy')

  const build = lines.reduce((acc, l) => add(acc, l.hours), [0, 0])
  const builtScaled = scale(build, stageFactor)
  const qa = scale(builtScaled, qaFactor)
  const pm = scale(add(builtScaled, qa), 0.12)
  const total = add(add(builtScaled, qa), pm)

  // 7. Team & timeline. Productive hours per person per week are ~30, not 40.
  const mid = (total[0] + total[1]) / 2
  let team = mid < 400 ? 2 : mid < 900 ? 3 : mid < 1600 ? 4 : 5
  let costFactor = 1
  if (input.priority === 'fast') {
    team += 1
    costFactor = 1.15
  }
  const weeks = [Math.max(3, round(total[0] / (team * 30))), Math.max(4, round(total[1] / (team * 30)))]

  const rate = Number(input.rate) > 0 ? Number(input.rate) : DEFAULT_RATE
  const cost = [round(total[0] * rate * costFactor), round(total[1] * rate * costFactor)]

  const phases = [
    { id: 'discovery', share: 0.08 },
    { id: 'design', share: apiOnly ? 0.04 : 0.12 },
    { id: 'development', share: apiOnly ? 0.63 : 0.55 },
    { id: 'qa', share: 0.15 },
    { id: 'launch', share: 0.1 },
  ].map((p) => ({ ...p, hours: scale(total, p.share) }))

  const byCategory = ['ai', 'core', 'ui', 'integration', 'ops'].map((c) => ({
    id: c,
    hours: lines.filter((l) => l.category === c).reduce((acc, l) => add(acc, l.hours), [0, 0]),
  })).filter((c) => c.hours[1] > 0)

  const roles = rolesFor(type, apiOnly, team)

  return { lines, build: builtScaled, qa, pm, total, weeks, team, cost, rate, phases, byCategory, roles, apiOnly }
}

// Who works on it. Shares are of the team's time, not headcount.
const rolesFor = (type, apiOnly, team) => {
  const r = []
  if (type.ml) r.push({ id: 'ml', share: 0.35 })
  r.push({ id: 'backend', share: type.ml ? 0.2 : 0.3 })
  if (!apiOnly) r.push({ id: 'frontend', share: type.ml ? 0.15 : 0.3 })
  if (!apiOnly) r.push({ id: 'design', share: 0.08 })
  r.push({ id: 'qa', share: 0.12 })
  r.push({ id: 'pm', share: 0.1 })
  const sum = r.reduce((a, x) => a + x.share, 0)
  return r.map((x) => ({ ...x, share: x.share / sum, people: Math.max(1, Math.round((x.share / sum) * team * 2) / 2) }))
}

// The hourly rate and the budget are in the currency the visitor picks; the
// number itself is not converted, so a rate of 40 means $40/h or €40/h.
export const CURRENCIES = { USD: '$', EUR: '€' }

// European visitors see euros first. The time zone is the one hint available
// without asking, and it is only a default — the visitor can switch.
export const defaultCurrency = () => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    return zone.startsWith('Europe/') ? 'EUR' : 'USD'
  } catch (e) {
    return 'USD'
  }
}

export const formatMoney = (n, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n)

export const formatRange = ([a, b], unit = '') => `${a.toLocaleString('en-US')}–${b.toLocaleString('en-US')}${unit}`
